import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Edit, Trash2, Copy, Link, Users, Route } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { SearchInput, StatusBadge, LoadingState, EmptyState } from '@/components/shared';
import { Calendar } from 'lucide-react';
import EventDetailManager from '../EventDetailManager';
import { EventsManagerViewProps } from './eventsmanager.props';
import EventsFormDialog from './eventsmanager.form';

const EventsManagerView: React.FC<EventsManagerViewProps> = ({
  loading,
  searchQuery,
  setSearchQuery,
  filteredEvents,
  isDialogOpen,
  setIsDialogOpen,
  editingEvent,
  setEditingEvent,
  formData,
  setFormData,
  handleSubmit,
  handleEdit,
  handleDelete,
  deleteTarget,
  setDeleteTarget,
  confirmDelete,
  organizers,
  allSeries,
  isOrganizer,
  user,
  selectedEvent,
  setSelectedEvent,
  isRegistrationOpen,
  getRegStatusVariant,
  getRegStatusLabel,
  getEventCounts,
}) => {
  if (selectedEvent) {
    return (
      <EventDetailManager
        event={selectedEvent}
        onBack={() => setSelectedEvent(null)}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Управување со настани</CardTitle>
            <EventsFormDialog
              isDialogOpen={isDialogOpen}
              setIsDialogOpen={setIsDialogOpen}
              editingEvent={editingEvent}
              setEditingEvent={setEditingEvent}
              formData={formData}
              setFormData={setFormData}
              handleSubmit={handleSubmit}
              deleteTarget={deleteTarget}
              setDeleteTarget={setDeleteTarget}
              confirmDelete={confirmDelete}
              organizers={organizers}
              allSeries={allSeries}
              isOrganizer={isOrganizer}
              user={user}
            />
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <LoadingState />
          ) : (
            <>
              <div className="mb-4">
                <SearchInput value={searchQuery} onChange={setSearchQuery} placeholder="Пребарувај настани..." />
              </div>
              <div className="space-y-4">
                {filteredEvents.length === 0 ? (
                  <EmptyState
                    icon={<Calendar className="w-10 h-10 text-muted-foreground" />}
                    message={searchQuery ? 'Нема настани за пребарувањето.' : 'Нема креирано настани. Додајте го вашиот прв настан.'}
                    action={!searchQuery ? { label: 'Додај настан', onClick: () => setIsDialogOpen(true) } : undefined}
                  />
                ) : filteredEvents.map((event) => (
                  <div
                    key={event.id}
                    className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-muted transition-colors cursor-pointer"
                    onClick={() => setSelectedEvent(event)}
                  >
                    <div className="flex items-center space-x-4">
                      <img src={event.image} alt={event.title} className="w-16 h-16 object-cover rounded" />
                      <div>
                        <h4 className="font-semibold text-foreground">{event.title}</h4>
                        <p className="text-sm text-muted-foreground">
                          {event.organizerName ? `${event.organizerName} • ` : ''}{event.location} • {event.date} • {event.time}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-muted-foreground font-mono select-all" title="Event GUID">{event.id}</span>
                          <button
                            type="button"
                            className="text-muted-foreground hover:text-foreground"
                            title="Копирај GUID"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(event.id);
                              toast({ title: 'Копирано', description: 'Event GUID е копиран.' });
                            }}
                          >
                            <Copy className="w-3 h-3" />
                          </button>
                          <button
                            type="button"
                            className="text-blue-400 hover:text-blue-600"
                            title="Копирај линк за регистрација"
                            onClick={(e) => {
                              e.stopPropagation();
                              const url = `${window.location.origin}/?event=${event.id}#register`;
                              navigator.clipboard.writeText(url);
                              toast({ title: 'Копирано', description: 'Линкот за регистрација е копиран.' });
                            }}
                          >
                            <Link className="w-3 h-3" />
                          </button>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <StatusBadge
                            label={event.status === 'upcoming' ? 'Следен' : 'Поминат'}
                            variant={event.status === 'upcoming' ? 'success' : 'neutral'}
                          />
                          {isRegistrationOpen(event) && (
                            <StatusBadge
                              label={getRegStatusLabel(isRegistrationOpen(event)!)}
                              variant={getRegStatusVariant(isRegistrationOpen(event)!)}
                            />
                          )}
                          {(() => {
                            const counts = getEventCounts(event.id);
                            const tshirtGroups = [
                              { key: 'male', label: 'Машки', data: counts.tshirtSizes.male },
                              { key: 'female', label: 'Женски', data: counts.tshirtSizes.female },
                              { key: 'kids', label: 'Детски', data: counts.tshirtSizes.kids },
                              { key: 'unknown', label: 'Непознато', data: counts.tshirtSizes.unknown },
                            ];
                            return (
                              <>
                                {counts.raceTypes > 0 && (
                                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                                    <Route className="w-3 h-3" />{counts.raceTypes} тип{counts.raceTypes !== 1 ? 'ови' : ''}
                                  </span>
                                )}
                                {counts.registrations > 0 && (
                                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                                    <Users className="w-3 h-3" />{counts.registrations} рег.
                                  </span>
                                )}
                                {counts.registrationByRaceType.length > 0 && (
                                  <div className="w-full mt-1 space-y-1 text-xs text-muted-foreground">
                                    <p className="font-medium text-foreground/80">Регистрации по тип на трка:</p>
                                    {counts.registrationByRaceType.map((raceTypeSummary) => (
                                      <div key={raceTypeSummary.raceTypeId} className="flex flex-wrap items-center gap-1">
                                        <span className="font-medium">{raceTypeSummary.raceTypeName}: {raceTypeSummary.total}</span>
                                        {Object.entries(raceTypeSummary.paymentStatuses)
                                          .sort(([a], [b]) => {
                                            if (a === 'Paid') return -1;
                                            if (b === 'Paid') return 1;
                                            return a.localeCompare(b);
                                          })
                                          .map(([status, total]) => (
                                            <span key={`${raceTypeSummary.raceTypeId}-${status}`} className="bg-muted px-1.5 py-0.5 rounded">
                                              {status}: {total}
                                            </span>
                                          ))}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {counts.tshirtSizes.total > 0 && (
                                  <div className="w-full mt-1 space-y-1 text-xs text-muted-foreground">
                                    <p className="font-medium text-foreground/80">Маички (вкупно: {counts.tshirtSizes.total})</p>
                                    {tshirtGroups
                                      .filter((group) => group.data.total > 0)
                                      .map((group) => (
                                        <div key={group.key} className="flex flex-wrap items-center gap-1">
                                          <span className="font-medium">{group.label}: {group.data.total}</span>
                                          {Object.entries(group.data.sizes)
                                            .sort(([a], [b]) => a.localeCompare(b))
                                            .map(([size, total]) => (
                                              <span key={`${group.key}-${size}`} className="bg-muted px-1.5 py-0.5 rounded">
                                                {size}: {total}
                                              </span>
                                            ))}
                                        </div>
                                      ))}
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleEdit(event); }}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleDelete(event.id); }} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default EventsManagerView;
